/* ============================================================
   CRC PDP SECTIONS JS
   - Records recently-viewed product handles (localStorage)
   - Renders Recently Viewed cards via Section Rendering API
   - Pointer-drag carousel for both rows (.crc-pdp-row + the
     related-products .product-grid)
   - Scroll-in reveal (right = recently viewed, left = related)
   - Re-inits after the async related-products feed injects
   ============================================================ */
(function () {
  'use strict';
  if (window.__crcPdpSectionsInit) return;
  window.__crcPdpSectionsInit = true;

  var RV_KEY = 'crc_rv_handles';
  var RV_MAX = 25;
  var reduceMotion = window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- localStorage helpers ---------- */
  function readList() {
    try { return JSON.parse(localStorage.getItem(RV_KEY)) || []; }
    catch (e) { return []; }
  }
  function writeList(list) {
    try { localStorage.setItem(RV_KEY, JSON.stringify(list)); } catch (e) {}
  }

  /* ---------- 1. RECORD current product ---------- */
  function recordCurrent() {
    var el = document.querySelector('crc-recently-viewed[data-current-handle]');
    var handle = el ? el.getAttribute('data-current-handle') : null;
    // fall back to the product-info element on the PDP
    if (!handle) {
      var pi = document.querySelector('product-info[data-url]');
      if (pi) {
        var m = (pi.getAttribute('data-url') || '').match(/\/products\/([^\/?#]+)/);
        if (m) handle = m[1];
      }
    }
    if (!handle) return;
    var list = readList().filter(function (h) { return h && h !== handle; });
    list.unshift(handle);
    if (list.length > RV_MAX) list = list.slice(0, RV_MAX);
    writeList(list);
  }

  /* ---------- 2. RENDER recently viewed ---------- */
  function renderRecentlyViewed() {
    var host = document.querySelector('crc-recently-viewed');
    if (!host || host.dataset.crcRendered === '1') return;
    host.dataset.crcRendered = '1';

    var current = host.getAttribute('data-current-handle');
    var limit = parseInt(host.getAttribute('data-limit'), 10) || 8;
    var row = host.querySelector('.crc-rv__row');
    if (!row) return;

    var handles = readList().filter(function (h) {
      return h && h !== current;
    }).slice(0, limit);
    if (!handles.length) return;

    Promise.all(handles.map(function (h) {
      return fetch('/products/' + encodeURIComponent(h) +
        '?section_id=crc-recently-viewed-card')
        .then(function (r) { return r.ok ? r.text() : ''; })
        .catch(function () { return ''; });
    })).then(function (htmls) {
      var tmp = document.createElement('div');
      tmp.innerHTML = htmls.join('');
      // Section Rendering API wraps output in #shopify-section-...; pull <li>s
      var items = tmp.querySelectorAll('li.grid__item');
      if (!items.length) return;
      var order = 0;
      items.forEach(function (li) {
        li.style.setProperty('--crc-order', order++);
        row.appendChild(li);
      });
      host.hidden = false;
      initCarousel(row);
      observeReveal(host);
    });
  }

  /* ---------- 3. POINTER-DRAG CAROUSEL ---------- */
  function initCarousel(row) {
    if (!row || row.__crcCarousel) return;
    row.__crcCarousel = true;
    var isDown = false, startX = 0, startScroll = 0, moved = false, captured = false, pid = null;
    var DRAG_THRESHOLD = 4;

    row.addEventListener('pointerdown', function (e) {
      if (e.pointerType !== 'mouse') return; // touch/trackpad use native snap
      isDown = true; moved = false; captured = false; pid = e.pointerId;
      startX = e.clientX;
      startScroll = row.scrollLeft;
      // NOTE: do NOT setPointerCapture here — capturing on mousedown retargets
      // the subsequent click away from the card's <a>, so plain clicks would
      // never navigate. We only capture once an actual drag begins, below.
    });
    row.addEventListener('pointermove', function (e) {
      if (!isDown) return;
      var walk = e.clientX - startX;
      // Scroll 1:1 with the cursor from the very first pixel so the grab feels
      // immediate (previously it waited for a 6px threshold before moving at
      // all, which felt unresponsive). A plain click moves <4px — an
      // imperceptible scroll — and still navigates because `moved` never trips.
      row.scrollLeft = startScroll - walk;
      if (!moved && Math.abs(walk) > DRAG_THRESHOLD) {
        moved = true;
        row.classList.add('is-dragging');
        try { row.setPointerCapture(pid); captured = true; } catch (err) {}
      }
    });
    function end() {
      if (!isDown) return;
      isDown = false;
      if (captured) { try { row.releasePointerCapture(pid); } catch (err) {} captured = false; }
      requestAnimationFrame(function () { row.classList.remove('is-dragging'); });
    }
    row.addEventListener('pointerup', end);
    row.addEventListener('pointercancel', end);
    row.addEventListener('pointerleave', end);
    // Swallow the click that immediately follows a *drag* so we don't also
    // follow a card link. A plain click never sets `moved`, so it passes through
    // and the link navigates normally.
    row.addEventListener('click', function (e) {
      if (moved) { e.preventDefault(); e.stopPropagation(); }
      moved = false;
    }, true);
  }

  /* ---------- 4. SCROLL-IN REVEAL ---------- */
  function observeReveal(el) {
    if (!el || el.__crcRevealBound) return;
    el.__crcRevealBound = true;

    if (reduceMotion || !('IntersectionObserver' in window)) {
      el.classList.add('crc-revealed');
      return;
    }
    var io = new IntersectionObserver(function (entries, obs) {
      entries.forEach(function (en) {
        if (!en.isIntersecting) return;
        var cards = en.target.querySelectorAll('.crc-pdp-row > .grid__item, .product-grid > .grid__item');
        cards.forEach(function (c, i) { c.style.setProperty('--crc-order', i); });
        en.target.classList.add('crc-revealed');
        obs.unobserve(en.target);
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -40px 0px' });
    io.observe(el);
  }

  /* ---------- 5. RELATED-PRODUCTS (async feed) ---------- */
  function hookRelated() {
    var pr = document.querySelector('product-recommendations.crc-pdp-carousel');
    if (!pr || pr.__crcHooked) return;
    pr.__crcHooked = true;

    function onLoaded() {
      var row = pr.querySelector('.product-grid');
      if (row) initCarousel(row);
      observeReveal(pr);
    }
    if (pr.classList.contains('product-recommendations--loaded')) {
      onLoaded();
    } else {
      var mo = new MutationObserver(function () {
        if (pr.classList.contains('product-recommendations--loaded')) {
          mo.disconnect();
          onLoaded();
        }
      });
      mo.observe(pr, { attributes: true, attributeFilter: ['class'] });
      // safety: also try after a few ticks in case class is already set
      var tries = 0;
      var iv = setInterval(function () {
        if (pr.classList.contains('product-recommendations--loaded') || tries++ > 20) {
          clearInterval(iv);
          if (pr.classList.contains('product-recommendations--loaded')) onLoaded();
        }
      }, 300);
    }
  }

  /* ---------- INIT ---------- */
  function init() {
    recordCurrent();
    renderRecentlyViewed();
    hookRelated();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', function () {
    // editor: allow re-render of a freshly added section
    var host = document.querySelector('crc-recently-viewed');
    if (host) host.dataset.crcRendered = '';
    init();
  });
})();
